import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { API } from "@/lib/api";
import AtlasView, { LENS_COLORS } from "@/components/AtlasView";
import PassportCard from "@/components/PassportCard";
import PublicLayout from "@/components/PublicLayout";
import { useSeo } from "@/lib/seo";
import { GlobeHemisphereWest, SpinnerGap, MapPin, House, AirplaneTilt } from "@phosphor-icons/react";

const LENS_BTN = [
  { v: "visited", l: "Visited", icon: MapPin }, { v: "lived", l: "Lived", icon: House },
  { v: "next", l: "Next", icon: AirplaneTilt },
];

export default function PublicAtlas() {
  const { slug } = useParams();
  const [data, setData] = useState(undefined);
  const [lensFilter, setLensFilter] = useState("all");

  useEffect(() => {
    axios.get(`${API}/public/atlas/${slug}`).then((r) => setData(r.data)).catch(() => setData(null));
  }, [slug]);

  useSeo({
    title: data?.map ? `${data.map.name} — Living Atlas` : "Living Atlas",
    description: data?.map?.description || "A living travel atlas on PhotoPulse.pro",
  });

  return (
    <PublicLayout>
      <div className="mx-auto max-w-[1200px] px-5 py-8">
        {data === undefined ? (
          <div className="grid h-[50vh] place-items-center"><SpinnerGap size={30} className="animate-spin text-[#F27A54]" /></div>
        ) : data === null ? (
          <div className="grid h-[50vh] place-items-center text-center">
            <GlobeHemisphereWest size={48} weight="thin" className="text-zinc-300" />
            <p className="mt-3 font-display font-bold">Map not found</p>
            <p className="text-sm text-muted-foreground">This atlas may be private or no longer exists.</p>
          </div>
        ) : (
          <>
            <div className="flex items-center gap-2 text-[#F27A54]">
              <GlobeHemisphereWest size={22} weight="fill" />
              <p className="overline text-[10px]">Living Atlas{data.map.owner_name ? ` · by ${data.map.owner_name}` : ""}</p>
            </div>
            <h1 className="mt-1 font-display text-3xl font-black tracking-tight">{data.map.name}</h1>
            {data.map.description && <p className="mt-1 max-w-2xl text-sm text-muted-foreground">{data.map.description}</p>}

            <div className="mt-4 flex flex-wrap gap-2">
              <button data-testid="pub-lens-all" onClick={() => setLensFilter("all")}
                className={`rounded-full px-3.5 py-1.5 text-xs font-bold transition-colors ${lensFilter === "all" ? "bg-zinc-900 text-white" : "bg-zinc-100 text-zinc-600 hover:bg-zinc-200"}`}>All lenses</button>
              {LENS_BTN.map((b) => (
                <button key={b.v} data-testid={`lens-${b.v}`} onClick={() => setLensFilter(b.v)}
                  className={`flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-xs font-bold transition-colors ${lensFilter === b.v ? "text-white" : "bg-zinc-100 text-zinc-600 hover:bg-zinc-200"}`}
                  style={lensFilter === b.v ? { background: LENS_COLORS[b.v] } : {}}>
                  <b.icon size={12} weight="fill" /> {b.l}
                </button>
              ))}
            </div>

            <div className="mt-4 grid gap-5 lg:grid-cols-[1fr_340px]">
              <AtlasView countries={data.countries} points={data.points} lensFilter={lensFilter} photoBase="/p" height="62vh" />
              <PassportCard stats={data.stats} />
            </div>
          </>
        )}
      </div>
    </PublicLayout>
  );
}
